import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CRM_PIPELINE_STATUSES } from '@bcc/shared';
import type { CrmContactWithMeta } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_TILE } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

/** Kanban posture — kinetic pipeline hygiene for disciplined capital sequencing. */

const STAGES = Object.values(CRM_PIPELINE_STATUSES);

export function CrmPipeline() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [contacts, setContacts] = useState<CrmContactWithMeta[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }));

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('type', typeFilter);
      const qs = params.toString();
      const res = await apiFetch<{ contacts: CrmContactWithMeta[] }>(
        `/api/crm/contacts${qs ? `?${qs}` : ''}`,
        { token }
      );
      setContacts(res.contacts);
    })();
  }, [token, typeFilter]);

  const grouped = useMemo(() => {
    const bucket: Record<string, CrmContactWithMeta[]> = {};
    STAGES.forEach((s) => {
      bucket[s] = [];
    });
    for (const c of contacts) {
      if (!bucket[c.status]) bucket[c.status] = [];
      bucket[c.status].push(c);
    }
    return bucket;
  }, [contacts]);

  async function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || !token) return;
    const contactId = String(active.id);
    const maybeStage = String(over.id);
    if (!(STAGES as readonly string[]).includes(maybeStage)) return;
    const current = contacts.find((x) => x.id === contactId);
    if (!current || current.status === maybeStage) return;

    await apiFetch(`/api/crm/contacts/${contactId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ status: maybeStage }),
    });
    setContacts((rows) =>
      rows.map((r) =>
        r.id === contactId ? { ...r, status: maybeStage as CrmContactWithMeta['status'] } : r
      )
    );
  }

  return (
    <div className="space-y-8 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Pipeline board</h1>
          <p className="mt-2 max-w-xl text-sm text-[var(--home-muted-on-dark)]">
            Drag dossiers across institutional gates — persists server-side audit trail compatible with capital
            compliance reviews.
          </p>
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className={SITE_FORM_CONTROL}
        >
          <option value="all">All counterpart types</option>
          <option value="lead">Leads</option>
          <option value="investor">Investors</option>
          <option value="broker">Brokers</option>
          <option value="owner">Owners</option>
        </select>
      </div>

      <DndContext collisionDetection={closestCorners} sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
          {STAGES.map((stage) => (
            <KanbanLane key={stage} stage={stage}>
              {(grouped[stage] ?? []).map((c) => (
                <KanbanCard key={c.id} contact={c} />
              ))}
            </KanbanLane>
          ))}
        </div>
      </DndContext>
    </div>
  );
}

function KanbanLane({
  stage,
  children,
}: {
  stage: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[360px] p-3 shadow-[0_18px_50px_-30px_rgb(0_0_0/0.5)] transition-colors ${
        isOver
          ? `${SITE_GLASS_TILE} border-[var(--home-miami-blue)]/65 bg-[var(--home-zone-dark)]/60 ring-1 ring-[var(--home-miami-blue)]/35`
          : SITE_GLASS_TILE
      }`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--home-muted-on-dark)] mb-4">{stage}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function KanbanCard({ contact }: { contact: CrmContactWithMeta }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: contact.id });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={style}>
      <div className="cursor-grab rounded-xl border border-white/[0.1] bg-white/[0.06] p-3 shadow-inner shadow-black/25 backdrop-blur-md transition-colors hover:border-[var(--home-miami-blue)]/45 active:cursor-grabbing">
        <p className="text-sm font-medium leading-snug text-[var(--home-text-on-dark)]">{contact.full_name}</p>
        <p className="mt-1 text-[11px] text-[var(--home-muted-on-dark)]">{contact.email ?? '—'}</p>
        <div className="mt-3 flex justify-between gap-3 text-[11px] uppercase text-emerald-200/85">
          <span>{contact.type}</span>
          {(contact.tags ?? []).slice(0, 2).map((t) => (
            <span key={t} className="truncate font-mono text-[10px] text-[var(--home-muted-on-dark)]">
              {t}
            </span>
          ))}
        </div>
        <Link
          to={`/admin/crm/contacts/${contact.id}`}
          className="mt-3 inline-block text-[11px] font-medium text-[var(--home-miami-blue)] hover:brightness-110"
          onClick={(evt) => evt.stopPropagation()}
        >
          dossier →
        </Link>
      </div>
    </div>
  );
}
